import { KEY_DOWN, KEY_N, KEY_P, KEY_RETURN, KEY_UP } from "keycode-js";
import { useCallback, useMemo, useState } from "react";

import { useHotKey, type HotKeyDefinition } from "~/client/components/HotKey";

enum Command {
  SELECT_NEXT,
  SELECT_PREV,
  SUBMIT,
}

const KeyDefinitions: ReadonlyArray<HotKeyDefinition<Command>> = [
  {
    keyCode: KEY_N,
    modifier: true,
    command: Command.SELECT_NEXT,
  },
  {
    keyCode: KEY_DOWN,
    command: Command.SELECT_NEXT,
  },
  {
    keyCode: KEY_P,
    modifier: true,
    command: Command.SELECT_PREV,
  },
  {
    keyCode: KEY_UP,
    command: Command.SELECT_PREV,
  },
  {
    keyCode: KEY_RETURN,
    command: Command.SUBMIT,
  },
];

type SetSelectedIndex = (index: number | null) => void;

const selectNextItem = (selectedIndex: number | null, setSelectedIndex: SetSelectedIndex, itemCount: number) => {
  if (itemCount === 0) {
    return;
  }
  if (selectedIndex === null) {
    setSelectedIndex(0);
    return;
  }
  setSelectedIndex(Math.min(selectedIndex + 1, itemCount - 1));
};

const selectPrevItem = (selectedIndex: number | null, setSelectedIndex: SetSelectedIndex, itemCount: number) => {
  if (itemCount === 0) {
    return;
  }
  if (selectedIndex === null) {
    return;
  }
  const newIndex = selectedIndex - 1;
  if (newIndex < 0) {
    setSelectedIndex(null);
    return;
  }
  setSelectedIndex(newIndex);
};

const handleDispatch = (
  command: Command,
  selectedIndex: number | null,
  setSelectedIndex: SetSelectedIndex,
  itemCount: number,
  setIsSubmitting: (b: boolean) => void,
) => {
  switch (command) {
    case Command.SELECT_NEXT:
      selectNextItem(selectedIndex, setSelectedIndex, itemCount);
      return;
    case Command.SELECT_PREV:
      selectPrevItem(selectedIndex, setSelectedIndex, itemCount);
      return;
    case Command.SUBMIT:
      setIsSubmitting(true);
      return;
  }
};

type ItemLocation = {
  listIndex: number;
  itemIndex: number;
};

type IsItemSelected = (candidate: ItemLocation) => boolean;
const generateIsItemSelected = (selected: ItemLocation | null): IsItemSelected => {
  return (candidate: ItemLocation): boolean => {
    if (selected === null) {
      return false;
    }
    return candidate.listIndex === selected.listIndex && candidate.itemIndex === selected.itemIndex;
  };
};

export const useSelectListItemByKey = (itemCounts: ReadonlyArray<number>) => {
  const itemLocations: ReadonlyArray<ItemLocation> = useMemo(
    () =>
      itemCounts.flatMap((itemCount, listIndex) => {
        return [...Array(itemCount)].map((_, itemIndex) => ({
          listIndex,
          itemIndex,
        }));
      }),
    [itemCounts],
  );

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onDispatch = useCallback(
    (command: Command) => {
      handleDispatch(command, selectedIndex, setSelectedIndex, itemLocations.length, setIsSubmitting);
    },
    [selectedIndex, setSelectedIndex, itemLocations, setIsSubmitting],
  );
  useHotKey({ definitions: KeyDefinitions, onDispatch });

  const isItemSelected = useMemo(
    () => generateIsItemSelected(selectedIndex === null ? null : itemLocations[selectedIndex]),
    [selectedIndex, itemLocations],
  );

  return [isItemSelected, isSubmitting, selectedIndex !== null] as const;
};

export type ListItemSelection = {
  isItemSelected: IsItemSelected;
  isSubmitting: boolean;
};
